import React, { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import {
  extractVacancyFromScreenshot,
  extractVacancyFromPdf,
  type ExtractedVacancy,
  LlmError,
} from "../../lib/ai/vacancy-extraction";
import { extractPdfText } from "../../lib/fs/pdf";
import { resolveLlmConfigs } from "../../lib/fs/config";
import VacancyModal from "./VacancyModal";

type InputMode = "screenshot" | "pdf";

export default function VacancyEvaluator() {
  const t = useT();
  const settings = useAppStore((s) => s.settings);
  const [, navigate] = useLocation();
  const activeLlmId = useAppStore((s) => s.activeLlmId);

  const [inputMode, setInputMode] = useState<InputMode>("screenshot");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [pdfText, setPdfText] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedVacancy | null>(null);

  // Check whether the active Ollama config supports vision
  const activeConfig = settings ? resolveLlmConfigs(settings).find((c) => c.id === activeLlmId) : null;
  const isOllamaNoVision =
    activeConfig?.provider === "ollama" &&
    !["llava", "llama3.2-vision", "moondream", "qwen2-vl", "minicpm-v", "bakllava"].some((v) =>
      activeConfig.model.toLowerCase().includes(v),
    );

  // Global paste → screenshot
  useEffect(() => {
    function handleGlobalPaste(e: ClipboardEvent) {
      if (loading || showModal) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file) {
            readImageFile(file);
            setInputMode("screenshot");
          }
        }
      }
    }
    window.addEventListener("paste", handleGlobalPaste);
    return () => window.removeEventListener("paste", handleGlobalPaste);
  }, [loading, showModal]);

  function readImageFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      setScreenshot(e.target?.result as string);
      setPdfText(null);
      setPdfFileName(null);
      setErrorMsg(null);
    };
    reader.readAsDataURL(file);
  }

  async function readPdfFile(file: File) {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setErrorMsg("Please upload a PDF file.");
      return;
    }
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const result = await extractPdfText(bytes);

      if (result.wasScanned) {
        setErrorMsg(
          "PDF appears to be a scan with no text layer — using image mode instead. Consider pasting a screenshot for better results.",
        );
        return;
      }

      setPdfText(result.text);
      setPdfFileName(file.name);
      setScreenshot(null);
      setInputMode("pdf");
      setErrorMsg(
        result.truncated
          ? `PDF truncated to first 10 pages (total: ${result.pageCount}). Vacancy text is typically on the first page.`
          : null,
      );
    } catch (err: any) {
      setErrorMsg(`Failed to read PDF: ${err?.message || String(err)}`);
    }
  }

  function handleImageFile(file: File) {
    if (!file.type.startsWith("image/")) {
      setErrorMsg("Please upload a screenshot image (PNG/JPG).");
      return;
    }
    readImageFile(file);
  }

  function onDragOver(e: React.DragEvent) { e.preventDefault(); }

  function onDropScreenshot(e: React.DragEvent) {
    e.preventDefault();
    if (loading || showModal) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageFile(file);
  }

  function onDropPdf(e: React.DragEvent) {
    e.preventDefault();
    if (loading || showModal) return;
    const file = e.dataTransfer.files?.[0];
    if (file) readPdfFile(file);
  }

  function openFilePicker(accept: string, handler: (f: File) => void) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = (e: any) => {
      if (e.target.files?.[0]) handler(e.target.files[0]);
    };
    input.click();
  }

  const hasInput = inputMode === "screenshot" ? !!screenshot : !!pdfText;

  async function handleAnalyse() {
    if (!hasInput) {
      setErrorMsg("Please provide a screenshot or PDF first.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      let result: ExtractedVacancy;

      if (inputMode === "pdf" && pdfText) {
        result = await extractVacancyFromPdf(pdfText, url, settings?.prompts?.vacancy_extraction);
      } else {
        result = await extractVacancyFromScreenshot(
          "", "", // deprecated params — ignored by client
          screenshot!,
          url,
          settings?.prompts?.vacancy_extraction,
        );
      }

      setExtractedData(result);
      setShowModal(true);
    } catch (err: any) {
      if (err instanceof LlmError && err.code === "no_vision") {
        setErrorMsg(
          "Screenshot input requires a vision-capable model. Switch to a different model in the header, or use PDF input instead.",
        );
      } else if (err instanceof LlmError && err.code === "quota_exceeded") {
        setErrorMsg("Quota exceeded on the active model. Switch to a different model using the selector in the header.");
      } else {
        setErrorMsg(`AI extraction failed: ${err?.message || String(err)}`);
      }
    } finally {
      setLoading(false);
    }
  }

  const noApiConfig = !settings?.llm_configs?.length && !settings?.anthropic_api_key;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">

      {/* Header & Back Button */}
      <div className="flex items-center gap-4 border-b border-slate-200/60 pb-5">
        <button
          onClick={() => navigate("/")}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors shadow-2xs cursor-pointer focus:outline-none"
          title="Back to Dashboard"
        >
          ←
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Vacancy Evaluator
          </h1>
          <p className="mt-1 text-xs text-slate-400">
            Capture vacancy requirements from a screenshot paste or a PDF print of the page.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">

        {/* Input area — 2 columns */}
        <div className="md:col-span-2 space-y-4">

          {/* Input mode tabs */}
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 dark:bg-slate-800/60 p-1 w-fit">
            {(["screenshot", "pdf"] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => { setInputMode(mode); setErrorMsg(null); }}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer focus:outline-none ${
                  inputMode === mode
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
              >
                {mode === "screenshot" ? "📋 Screenshot" : "📄 PDF"}
              </button>
            ))}
          </div>

          {/* Screenshot drop zone */}
          {inputMode === "screenshot" && (
            <>
              {isOllamaNoVision && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 p-3 text-xs text-amber-700 dark:text-amber-400 font-medium">
                  ⚠ The active model (<span className="font-bold">{activeConfig?.model}</span>) does not support image input. Switch to a vision-capable model in the header, or use PDF input instead.
                </div>
              )}
              <div
                onDragOver={onDragOver}
                onDrop={onDropScreenshot}
                onClick={() => { if (!screenshot) openFilePicker("image/*", handleImageFile); }}
                className={`group relative flex min-h-[350px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-200 ${
                  screenshot
                    ? "border-slate-300 dark:border-slate-600 bg-slate-50/20 dark:bg-slate-800/20"
                    : "border-slate-300 dark:border-slate-600 hover:border-slate-400 bg-white dark:bg-slate-800/30 hover:bg-slate-50/50 cursor-pointer"
                }`}
              >
                {screenshot ? (
                  <div className="relative w-full h-full max-h-[400px] overflow-hidden rounded-lg flex items-center justify-center">
                    <img
                      src={screenshot}
                      alt="Pasted screenshot"
                      className="max-h-[350px] w-auto object-contain rounded-md shadow-xs border border-slate-200 dark:border-slate-600"
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); setScreenshot(null); }}
                      className="absolute top-2 right-2 rounded-full bg-slate-900/85 hover:bg-slate-900 text-white p-1.5 text-xs shadow-md transition-all cursor-pointer focus:outline-none"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 group-hover:bg-slate-200/80 transition-colors duration-200">
                      📋
                    </div>
                    <p className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t("evaluator.paste_prompt")}
                    </p>
                    <p className="mt-1.5 text-5xs font-bold uppercase tracking-wider text-slate-400">
                      Supports drag & drop · click to browse
                    </p>
                    <p className="mt-3 text-2xs text-slate-400 dark:text-slate-500">
                      Higher token cost · requires vision model
                    </p>
                  </div>
                )}
              </div>
              {screenshot && (
                <p className="text-2xs text-slate-400 text-center">Screenshot ready</p>
              )}
            </>
          )}

          {/* PDF drop zone */}
          {inputMode === "pdf" && (
            <>
              <div
                onDragOver={onDragOver}
                onDrop={onDropPdf}
                onClick={() => { if (!pdfText) openFilePicker(".pdf", readPdfFile); }}
                className={`group relative flex min-h-[350px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-200 ${
                  pdfText
                    ? "border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-900/10"
                    : "border-slate-300 dark:border-slate-600 hover:border-slate-400 bg-white dark:bg-slate-800/30 hover:bg-slate-50/50 cursor-pointer"
                }`}
              >
                {pdfText ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 text-xl">
                      ✓
                    </div>
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {pdfFileName ?? "PDF loaded"}
                    </p>
                    <p className="text-2xs text-slate-400">
                      {pdfText.length.toLocaleString()} characters extracted
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPdfText(null); setPdfFileName(null); }}
                      className="mt-1 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-3 py-1.5 text-2xs font-semibold text-slate-600 dark:text-slate-300 transition-colors cursor-pointer focus:outline-none"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 group-hover:bg-slate-200/80 transition-colors duration-200">
                      📄
                    </div>
                    <p className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Drop PDF or click to browse
                    </p>
                    <p className="mt-1.5 text-5xs font-bold uppercase tracking-wider text-slate-400">
                      Print the vacancy page as PDF · drag & drop here
                    </p>
                    <p className="mt-3 text-2xs text-slate-400 dark:text-slate-500">
                      Lower token cost · works with all models including Ollama
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* URL Input & Analyse Button — 1 column */}
        <div className="md:col-span-1 space-y-6">
          <section className="rounded-2xl border border-slate-200/60 dark:border-slate-700/50 bg-white dark:bg-slate-800/60 p-6 shadow-xs">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 tracking-tight border-b border-slate-100 dark:border-slate-700 pb-3">
              Extraction Details
            </h3>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="url" className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Vacancy URL (optional)
              </label>
              <input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("evaluator.url_placeholder")}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:border-slate-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Active model indicator */}
            {activeConfig && (
              <div className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-700 px-3 py-2">
                <p className="text-2xs text-slate-400 uppercase font-bold tracking-wider">Active model</p>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{activeConfig.label}</p>
              </div>
            )}

            {/* No config warning */}
            {noApiConfig && (
              <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-700/50 p-3 text-2xs text-amber-700 dark:text-amber-400 font-semibold leading-relaxed">
                ⚠️ No LLM configured. Add <code>llm_configs</code> to <code>config/settings.json</code>.
              </div>
            )}

            {/* Error Message */}
            {errorMsg && (
              <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-700/50 p-3">
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 leading-relaxed">{errorMsg}</p>
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={handleAnalyse}
                disabled={!hasInput || loading || noApiConfig}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-900 px-4 py-2.5 text-xs font-semibold shadow-xs hover:shadow-sm transition-all duration-150 cursor-pointer focus:outline-none disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-white dark:border-t-slate-900" />
                    Analysing…
                  </>
                ) : (
                  <>
                    <span>{t("vacancy.analyse")}</span>
                    <span>→</span>
                  </>
                )}
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs">
          <div className="flex flex-col items-center gap-3.5 rounded-2xl bg-white dark:bg-slate-800 px-8 py-6 shadow-xl text-center border border-slate-100 dark:border-slate-700 max-w-xs animate-in zoom-in-95 duration-200">
            <span className="h-9 w-9 animate-spin rounded-full border-3 border-slate-200 border-t-slate-800 dark:border-slate-600 dark:border-t-slate-100" />
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                {inputMode === "pdf" ? "Reading PDF" : "Reading Screenshot"}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {activeConfig?.label ?? "AI"} is extracting the vacancy details…
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Vacancy Review Modal */}
      {showModal && extractedData && (
        <VacancyModal
          extracted={extractedData}
          screenshot={screenshot ?? undefined}
          url={url}
          onClose={() => {
            setShowModal(false);
            setExtractedData(null);
          }}
        />
      )}
    </div>
  );
}
